import React from 'react';
import Navbar from '../components/Navbar';
import SeoHead from '../components/SeoHead';
import Footer from '../components/Footer';

const Privacy = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
      <SeoHead
        title="Politique de confidentialité - PulseCIO"
        description="Consultez la politique de confidentialité de PulseCIO, plateforme SaaS de centralisation des messages professionnels."
        breadcrumb={{ name: "Politique de confidentialité", path: "/privacy" }}
      />
      <Navbar />
      <main className="flex-1 container mx-auto px-6 py-20">
        <h1 className="text-3xl font-bold mb-6 text-black dark:text-white">
          Politique de confidentialité
        </h1>
        <div className="prose dark:prose-invert max-w-none">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Cette page décrit les traitements de données personnelles effectués dans le cadre du service PulseCIO.
          </p>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Responsable de traitement
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Responsable : Mathieu Lalande — exploitation en auto-entreprise. 
            Toutes les données collectées sont gérées par l'éditeur du service.
          </p>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Données collectées
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Nous collectons les types de données suivants :
          </p>
          <ul className="list-disc ml-6 mb-4 text-gray-700 dark:text-gray-300">
            <li><strong>Données d'identification :</strong> nom, prénom, adresse email</li>
            <li><strong>Données de connexion :</strong> identifiants, mots de passe (chiffrés), tokens d'authentification</li>
            <li><strong>Données de contenu :</strong> messages, notes, tâches, templates que vous créez</li>
            <li><strong>Données d'utilisation :</strong> logs de connexion, actions effectuées, préférences d'affichage</li>
            <li><strong>Données techniques :</strong> adresse IP, type de navigateur, système d'exploitation, cookies</li>
            <li><strong>Données de facturation :</strong> informations traitées par Stripe (nous ne stockons pas les données de carte bancaire)</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">Finalités</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Les données collectées servent aux finalités suivantes :
          </p>
          <ul className="list-disc ml-6 mb-4 text-gray-700 dark:text-gray-300">
            <li>Fourniture et gestion du service (authentification, stockage, synchronisation)</li>
            <li>Gestion des abonnements et facturation</li>
            <li>Support client et communication avec les utilisateurs</li>
            <li>Sécurité et prévention de la fraude</li>
            <li>Amélioration du service et résolution de bugs</li>
            <li>Respect des obligations légales et réglementaires</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Le backend (API) est implémenté en Node.js avec Express et communique avec la base de données MongoDB Atlas. 
            Aucune donnée n'est collectée à des fins publicitaires ou revendue à des tiers.
          </p>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Durée de conservation
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Les données liées au compte et aux messages sont conservées tant que le compte existe ou selon 
            les obligations légales. Les logs et données techniques sont conservés pour une durée limitée 
            nécessaire au fonctionnement et à la sécurité.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Hébergement et sécurité des données
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            L'infrastructure backend utilise Node.js/Express et la base de données est hébergée sur MongoDB Atlas. 
            Le front-end est hébergé sur Hostinger. Les connexions sont chiffrées (TLS).
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2 text-black dark:text-white">
            Chiffrement des données (AES-256-GCM)
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            <strong>Toutes vos données personnelles et professionnelles sont chiffrées au repos</strong> avec un 
            algorithme de chiffrement de niveau militaire (AES-256-GCM) :
          </p>
          <ul className="list-disc ml-6 mb-4 text-gray-700 dark:text-gray-300">
            <li><strong>Données personnelles chiffrées :</strong> emails, noms, prénoms</li>
            <li><strong>Contenus professionnels chiffrés :</strong> messages, titres, descriptions</li>
            <li><strong>Tâches et notifications chiffrées :</strong> todos, contenu des notifications</li>
            <li><strong>Organisations chiffrées :</strong> noms de tenants/organisations</li>
            <li><strong>Mots de passe hashés :</strong> bcrypt avec 10 rounds (irréversible)</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            <strong>Protection en cas de violation :</strong> Même en cas de vol de la base de données, vos données 
            restent illisibles sans la clé de chiffrement, qui est stockée séparément et jamais dans la base de données.
          </p>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Cette approche garantit une <strong>conformité RGPD maximale</strong> et protège vos données contre 
            les violations, même en cas d'accès non autorisé à notre infrastructure.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Partage avec des tiers
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Nous ne vendons ni ne louons vos données personnelles. Nous partageons vos données uniquement avec :
          </p>
          <ul className="list-disc ml-6 mb-4 text-gray-700 dark:text-gray-300">
            <li><strong>MongoDB Atlas :</strong> hébergement de la base de données</li>
            <li><strong>Hostinger :</strong> hébergement de l'application front-end</li>
            <li><strong>Stripe :</strong> traitement des paiements (sous-traitant conforme PCI-DSS)</li>
            <li><strong>Autorités compétentes :</strong> si requis par la loi ou pour protéger nos droits légaux</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Tous nos sous-traitants sont soumis à des obligations contractuelles strictes de protection des données.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Transferts internationaux de données
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Vos données peuvent être transférées et stockées dans des centres de données situés dans l'Union 
            Européenne et hors UE (notamment aux États-Unis via MongoDB Atlas et Stripe). Ces transferts sont 
            encadrés par des garanties appropriées (clauses contractuelles types de l'UE, certifications Privacy 
            Shield pour les prestataires américains). Vous pouvez obtenir plus d'informations sur ces garanties 
            en nous contactant.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">Sécurité</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Des mesures techniques et organisationnelles sont mises en place : chiffrement en transit (TLS), 
            authentification et gestion des accès, mises à jour régulières des dépendances, sauvegardes 
            automatiques, surveillance des accès, et tests de sécurité réguliers. Malgré ces mesures, aucun 
            système n'est totalement infaillible. En cas de violation de données, nous vous en informerons 
            conformément au RGPD.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Protection des mineurs
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Notre service est destiné aux personnes âgées de 18 ans ou plus. Nous ne collectons pas 
            sciemment de données personnelles de mineurs. Si vous êtes parent ou tuteur et que vous 
            découvrez que votre enfant nous a fourni des informations personnelles sans votre consentement, 
            veuillez nous contacter pour que nous puissions supprimer ces données.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Droits des personnes
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression, d'opposition, 
            de limitation du traitement et de portabilité des données vous concernant. Pour exercer ces droits, 
            contactez-nous à{' '}
            <a href="mailto:contact@pulsecio.com" className="text-blue-600 dark:text-blue-400 hover:underline">
              contact@pulsecio.com
            </a>.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Réclamation
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Vous avez également le droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de 
            l'Informatique et des Libertés) si vous estimez que le traitement de vos données personnelles n'est 
            pas conforme au RGPD.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 text-black dark:text-white">
            Délai de réponse
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Nous nous engageons à répondre à vos demandes d'exercice de droits dans un délai d'un mois suivant la 
            réception de votre demande. Ce délai peut être prolongé de deux mois en cas de complexité ou de nombre 
            élevé de demandes.
          </p>
        </div>
      </main>
      <div className="w-full flex justify-center mt-12 mb-2">
        <a href="/" className="inline-flex items-center px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition">
          ← Retour à la page principale
        </a>
      </div>
      <Footer />
    </div>
  );
};

export default Privacy;